'use client';

import { create } from 'zustand';
import { User } from '../types';
import { persist, createJSONStorage } from 'zustand/middleware';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  token: string | null;
  hasEmailConfig: boolean;
  isImpersonating: boolean;
  readonly: boolean;
  adminCredentials: { username: string; password: string } | null;
  setAuth: (
    user: User,
    isImpersonating?: boolean,
    adminCredentials?: { username: string; password: string } | null
  ) => void;
  clearAuth: () => void;
  setHasEmailConfig: (hasConfig: boolean) => void;
  hydrated: boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      token: null,
      hasEmailConfig: false,
      isImpersonating: false,
      readonly: false,
      adminCredentials: null,
      hydrated: false,
      setAuth: (
        user: User,
        isImpersonating = false,
        adminCredentials = undefined
      ) => {
        if (user && user.token) {
          set({
            user,
            isAuthenticated: true,
            token: user.token,
            hasEmailConfig: user.hasEmailConfig || false,
            isImpersonating,
            readonly: user.readonly || false,
            adminCredentials,
          });
          // Store admin credentials in sessionStorage for security (cleared on tab close)
          if (isImpersonating && adminCredentials) {
            sessionStorage.setItem(
              'admin-credentials',
              JSON.stringify(adminCredentials)
            );
          } else {
            sessionStorage.removeItem('admin-credentials');
          }
        }
      },
      setHasEmailConfig: (hasConfig: boolean) =>
        set({ hasEmailConfig: hasConfig }),
      clearAuth: () => {
        set({
          user: null,
          isAuthenticated: false,
          token: null,
          hasEmailConfig: false,
          isImpersonating: false,
          readonly: false,
          adminCredentials: null,
        });
        sessionStorage.removeItem('admin-credentials');
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        token: state.token,
        hasEmailConfig: state.hasEmailConfig,
        isImpersonating: state.isImpersonating,
        readonly: state.readonly,
        // Don't persist adminCredentials - it's stored in sessionStorage
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.hydrated = true;
          // Restore admin credentials from sessionStorage if impersonating
          if (state.isImpersonating) {
            try {
              const stored = sessionStorage.getItem('admin-credentials');
              if (stored) {
                state.adminCredentials = JSON.parse(stored);
              }
            } catch (e) {
              // Ignore errors
            }
          }
        }
      },
    }
  )
);

// Helper to check if the store has been hydrated
export const useIsHydrated = () => useAuthStore((state) => state.hydrated);
